// Un intento de subida de una foto con Storage.put (Amplify 5) que se puede abortar y que no
// esconde los cortes de red de una subida multipart.
import { Storage } from "aws-amplify";
import { getRetryDecider } from "@aws-amplify/core/internals/aws-client-utils";

const NETWORK_ERROR_MESSAGES = ["Network Error", "Request aborted", "Network timeout"];

// Corte de red del XHR. El S3 de Amplify 5 no los reintenta: su decisor busca name «Network error»
// y el XHR rechaza con message «Network Error».
export const isNetworkError = (error) =>
  error?.name === "AbortError" || NETWORK_ERROR_MESSAGES.some((message) => error?.message?.includes(message));

// El decisor de reintentos de Amplify, con su mismo criterio (throttling, 5xx, reloj desfasado);
// del error de S3 solo le hace falta el código.
const amplifyRetryDecider = getRetryDecider(async (response) => {
  if (!response || response.statusCode < 300) return undefined;
  const body = await response.body?.text?.();
  return { name: /<Code>([^<]+)<\/Code>/.exec(body || "")?.[1] ?? String(response.statusCode) };
});

const canceledError = () => Object.assign(new Error("canceled"), { name: "CanceledError" });

/**
 * Sube `file` a `key`. `credentials` puede ser una promesa. Storage.put ignora `signal`: al
 * abortarlo se corta la subida con Storage.cancel sobre la promesa del put.
 *
 * Por encima de 5 MiB Amplify sube en multipart. Si falla una parte, aborta el upload y después
 * lista sus partes; sobre un upload ya abortado listParts responde NoSuchUpload, y ese error
 * sustituye al original. Para no perder un corte de red, el put lleva un `retryDecider` (Amplify
 * pasa las opciones del put a cada petición a S3) que decide como el de Amplify y anota los errores
 * de transporte: si el put acaba en NoSuchUpload tras un corte, se rechaza con el corte.
 * Las otras partes en vuelo seguirían subiendo hasta su 404; todas comparten el abortSignal del
 * put, así que al rechazar se cancela el put y no ocupan conexiones que la cola ya da por libres.
 * No se cancela antes: el abortMultipartUpload de la limpieza usa el mismo signal.
 */
export async function putPhoto(key, file, { contentType, metadata, credentials, onProgress, signal }) {
  const transportErrors = [];
  let putPromise;
  let rejectAborted;
  const aborted = new Promise((_, reject) => { rejectAborted = reject; });
  aborted.catch(() => {});
  const onAbort = () => {
    rejectAborted(canceledError());
    if (putPromise) Storage.cancel(putPromise, "stalled");
  };
  if (signal?.aborted) throw canceledError();
  signal?.addEventListener("abort", onAbort);
  try {
    const resolvedCredentials = await Promise.race([credentials, aborted]);
    putPromise = Storage.put(key, file, {
      contentType,
      metadata,
      credentials: resolvedCredentials,
      retryDecider: (response, error) => {
        if (error && !error.__CANCEL__) transportErrors.push(error);
        return amplifyRetryDecider(response, error);
      },
      progressCallback: (progress) => onProgress?.(progress?.loaded || 0, progress?.total || file.size),
    });
    return await putPromise;
  } catch (error) {
    if (putPromise) Storage.cancel(putPromise, "settled");
    const cut = error?.name === "NoSuchUpload" ? transportErrors.find(isNetworkError) : undefined;
    if (cut) throw Object.assign(cut, { maskedBy: error });
    throw error;
  } finally {
    signal?.removeEventListener("abort", onAbort);
  }
}
